import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useApi } from "hooks/useApi";
import type { Granularity } from "components/charts";
import { useSalesData } from "contexts/SalesDataContext";

interface SalesResponseWithRange {
  range: { granularity: Granularity; availableGranularities: Granularity[] };
}

export const useSalesQuery = <T extends SalesResponseWithRange>(
  isbns: string[] | undefined,
  type: "amazon" | "market",
  initialGranularity: Granularity = "weekly",
) => {
  const api = useApi();
  const [preferredGranularity, setPreferredGranularity] =
    useState<Granularity>(initialGranularity);
  const { from, to } = useSalesData();

  const start = from ? format(from, "yyyy-MM-dd") : undefined;
  const end = to ? format(to, "yyyy-MM-dd") : undefined;

  // Dedupe then sort, so the same logical set hashes to one cache entry
  // regardless of order and the backend's `isbns.join(",")` is stable too.
  const normalizedIsbns = [...new Set(isbns ?? [])].sort();

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      type,
      normalizedIsbns,
      start,
      end,
      preferredGranularity,
    ] as const,
    queryFn: () =>
      api.books.getSales({
        isbns: normalizedIsbns,
        start: start!,
        end: end!,
        granularity: preferredGranularity,
        type,
      }),
    enabled: Boolean(start && end && normalizedIsbns.length),
  });

  // The union overload of `getSales` returns the union; the public wrappers
  // pick `type`, so we assert the matching response here in one place.
  const sales = data as T | undefined;

  return {
    sales,
    isLoading,
    isError,
    // The backend clamps the requested granularity into the available set for
    // the window and reports the effective value back, so the toggle reflects
    // what was actually served rather than what was asked for.
    granularity: sales?.range.granularity ?? preferredGranularity,
    availableGranularities: sales?.range.availableGranularities ?? [],
    setGranularity: setPreferredGranularity,
  };
};
